import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, ChevronDown } from "lucide-react";
import { doc, getDoc, collection, addDoc, getDocs, query, orderBy, serverTimestamp } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import { db } from "../firebaseConfig";
import { LOCAL_BOOKS } from "./Booksdata";
import { useCart } from "./CartContext";
import toast from "react-hot-toast";

const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { addToCart, currency, currencySymbol } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [descOpen, setDescOpen] = useState(false);

  const [activeTab, setActiveTab] = useState("reviews");
  const [reviews, setReviews] = useState([]);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [reviewForm, setReviewForm] = useState({ name: "", email: "", body: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [question, setQuestion] = useState({ name: "", email: "", body: "" });
  const [questionSubmitted, setQuestionSubmitted] = useState(false);

  const local = useMemo(() => {
    return LOCAL_BOOKS.find((b) => b.id === String(id));
  }, [id]);

  // Fetch reviews from Firestore
  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(db, "products", String(id), "reviews"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Fetch reviews error:", e);
        toast.error("Couldn't load reviews.");
      }
    })();
  }, [id]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!selectedRating) return;
    setSubmitting(true);
    try {
      const ref = collection(db, "products", String(id), "reviews");
      const newReview = { ...reviewForm, rating: selectedRating, createdAt: serverTimestamp() };
      const docRef = await addDoc(ref, newReview);
      setReviews((prev) => [{ id: docRef.id, ...newReview, createdAt: new Date() }, ...prev]);
      setReviewForm({ name: "", email: "", body: "" });
      setSelectedRating(0);
      setSubmitted(true);
    } catch (e) {
      console.error("Submit review error:", e);
      toast.error("Couldn't submit your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    try {
      // 1 — Send email via EmailJS
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          form_type: `Book Question (${id})`,
          name: question.name,
          email: question.email,
          phone: "—",
          position: "—",
          message: question.body,
          time: new Date().toLocaleString(),
        },
        PUBLIC_KEY
      );

      // 2 — Save to Firestore under products/{id}/questions
      await addDoc(collection(db, "products", String(id), "questions"), {
        name: question.name,
        email: question.email,
        body: question.body,
        createdAt: serverTimestamp(),
      });

      setQuestionSubmitted(true);
      setQuestion({ name: "", email: "", body: "" });
    } catch (err) {
      console.error("Question submit error:", err);
      toast.error("Couldn't send your question. Please try again.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const ref = doc(db, "products", String(id));
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setProduct(null);
          return;
        }

        const data = snap.data();
        if (data?.active === false) {
          setProduct(null);
          return;
        }

        setProduct({ id: snap.id, ...data });
      } catch (e) {
        console.error("Fetch product error:", e);
        setProduct(null);
        toast.error("Couldn't load this book. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const book = useMemo(() => {
    if (!product) return null;
    return {
      ...product,
      images: local?.images || [],
      image: local?.images?.[0] || null,
    };
  }, [product, local]);

  const displayPrice = useMemo(() => {
    if (!book) return 0;
    return Number(book?.prices?.[currency]) || 0;
  }, [book, currency]);

  if (!book) return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 p-10 bg-white">
      {Array.from({ length: 6 }).map((_, i) => (
        <BookCardSkeleton key={i} />
      ))}
    </div>
  );

  function BookCardSkeleton() {
  return (
    <div className="bg-brand rounded-xl shadow-md overflow-hidden animate-pulse">
      <div className="w-full h-72 bg-gray-200" />
      <div className="p-4">
        <div className="h-5 w-2/3 bg-gray-200 rounded mb-3" />
        <div className="h-4 w-1/3 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

if (loading) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 p-10 bg-white">
      {Array.from({ length: 6 }).map((_, i) => (
        <BookCardSkeleton key={i} />
      ))}
    </div>
  );
}

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <button onClick={() => navigate(-1)} className="p-2 m-4 bg-brand rounded">
        Back
      </button>

      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 p-6">
        {/* Left: Images */}
        <div>
          {book.images?.length ? (
            <>
              <img
                src={book.images[activeImage]}
                alt={book.title}
                className="w-full rounded shadow"
              />
              <div className="flex gap-2 mt-4">
                {book.images.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Thumbnail ${idx}`}
                    onClick={() => setActiveImage(idx)}
                    className={`w-16 h-16 object-cover rounded-md cursor-pointer border-2 ${
                      activeImage === idx ? "border-brand" : "border-gray-300"
                    }`}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="w-full h-80 bg-gray-200 flex items-center justify-center rounded">
              No images
            </div>
          )}
        </div>

        {/* Right: Info */}
        <div>
          <h2 className="text-2xl font-bold mb-2">{book.title}</h2>

          <p className="text-xl text-gray-600 mb-4">
            {currencySymbol} {displayPrice ? displayPrice.toFixed(2) : "—"}
          </p>

        

          {book.id === "book_2" && (
            <p className="mb-6 text-gray-700">Coming Soon...</p>
          )}
           {book.id === "book_1" && (
<p className="mb-6 text-gray-700">Selling fast! Grab yours now.</p> )}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center border rounded">
              <button
                className="px-3 py-1 text-lg"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                -
              </button>
              <span className="px-4">{quantity}</span>
              <button
                className="px-3 py-1 text-lg"
                onClick={() => setQuantity((q) => q + 1)}
              >
                +
              </button>
            </div>
          </div>

          <button
            disabled={book.id === "book_2"}
            className="w-full bg-brand text-black hover:bg-black hover:text-brand transition-all duration-300 py-3 font-bold shadow-md mb-3 disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300 disabled:hover:text-gray-500 disabled:cursor-not-allowed"
            onClick={() =>
              addToCart({
                id: book.id,
                title: book.title,
                prices: book.prices,
                image: book.image,
                quantity,
              })
            }
          >
            {book.id === "book_2" ? "UNAVAILABLE" : "ADD TO CART"}
          </button>

          <button
            disabled={book.id === "book_2"}
            className="w-full bg-black text-brand hover:bg-brand hover:text-black transition-all duration-300 py-3 font-bold shadow-md mb-6 disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300 disabled:hover:text-gray-500 disabled:cursor-not-allowed"
            onClick={() => {
              addToCart({ id: book.id, title: book.title, prices: book.prices, image: book.image, quantity });
              navigate("/checkout");
            }}
          >
            {book.id === "book_2" ? "UNAVAILABLE" : "BUY IT NOW"}
          </button>

          {/* Description Accordion */}
          <div>
            <button
              onClick={() => setDescOpen((o) => !o)}
              className="w-full flex items-center justify-between border border-gray-200 px-5 py-4 text-sm font-semibold tracking-widest uppercase hover:bg-gray-50 transition"
            >
              <span>Description</span>
              <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${descOpen ? "rotate-180" : ""}`} />
            </button>
            {descOpen && (
              <div className="border border-t-0 border-gray-200 px-5 py-4 text-gray-700 leading-relaxed whitespace-pre-line">
                {book.description || "No description available for this book yet."}
              </div>
            )}
          </div>

          {/* Optional: if these fields are not in Firestore yet, they will show blank */}
          <div className="text-sm text-gray-500 space-y-1 mt-4">
            {book.sku && <p>SKU: {book.sku}</p>}
            {book.category && <p>Category: {book.category}</p>}
            {Array.isArray(book.tags) && book.tags.length > 0 && (
              <p>Tags: {book.tags.join(", ")}</p>
            )}
          </div>
        </div>
      </div>

      {/* Reviews & Questions Section */}
      <section className="max-w-6xl mx-auto px-6 pb-20 mt-8">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab("reviews")}
            className={`px-6 py-3 text-sm font-semibold tracking-widest uppercase border-b-2 transition ${activeTab === "reviews" ? "border-black text-black" : "border-transparent text-gray-400 hover:text-black"}`}
          >
            Reviews ({reviews.length})
          </button>
          <button
            onClick={() => setActiveTab("questions")}
            className={`px-6 py-3 text-sm font-semibold tracking-widest uppercase border-b-2 transition ${activeTab === "questions" ? "border-black text-black" : "border-transparent text-gray-400 hover:text-black"}`}
          >
            Questions
          </button>
        </div>

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <div>
            {reviews.length === 0 && !submitted ? (
              <div className="text-center py-10">
                <div className="flex justify-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-8 h-8 text-gray-300" />
                  ))}
                </div>
                <p className="text-gray-400 text-sm">Be the first to review this product</p>
              </div>
            ) : (
              <div className="space-y-6 mb-10">
                {reviews.map((r) => (
                  <div key={r.id} className="border-b border-gray-100 pb-6">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < r.rating ? "text-brand fill-brand" : "text-gray-300"}`} />
                      ))}
                    </div>
                    <p className="font-semibold text-sm">{r.name}</p>
                    <p className="text-gray-600 text-sm mt-1">{r.body}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Review Form */}
            {submitted ? (
              <p className="text-center text-green-600 font-medium py-6">Thank you for your review!</p>
            ) : (
              <form onSubmit={handleSubmitReview} className="border-t border-gray-200 pt-8 space-y-4">
                <h3 className="text-lg font-semibold tracking-wide">Write a Review</h3>
                <div>
                  <p className="text-sm text-gray-500 mb-2">Your Rating <span className="text-red-500">*</span></p>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-7 h-7 cursor-pointer transition ${star <= (hoverRating || selectedRating) ? "text-brand fill-brand" : "text-gray-300"}`}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setSelectedRating(star)}
                      />
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="text" placeholder="Your Name *" required value={reviewForm.name}
                    onChange={(e) => setReviewForm((f) => ({ ...f, name: e.target.value }))}
                    className="border border-gray-200 rounded px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-brand" />
                  <input type="email" placeholder="Your Email *" required value={reviewForm.email}
                    onChange={(e) => setReviewForm((f) => ({ ...f, email: e.target.value }))}
                    className="border border-gray-200 rounded px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-brand" />
                </div>
                <textarea placeholder="Your Review *" required rows={4} value={reviewForm.body}
                  onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))}
                  className="w-full border border-gray-200 rounded px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-brand" />
                <button type="submit" disabled={submitting || !selectedRating}
                  className="bg-black text-brand px-8 py-3 font-bold text-sm tracking-widest uppercase hover:bg-brand hover:text-black transition disabled:opacity-50">
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Questions Tab */}
        {activeTab === "questions" && (
          <div>
            {questionSubmitted ? (
              <p className="text-center text-green-600 font-medium py-6">Your question has been submitted!</p>
            ) : (
              <form onSubmit={handleSubmitQuestion} className="space-y-4 max-w-xl">
                <h3 className="text-lg font-semibold tracking-wide">Ask a Question</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="text" placeholder="Your Name *" required value={question.name}
                    onChange={(e) => setQuestion((q) => ({ ...q, name: e.target.value }))}
                    className="border border-gray-200 rounded px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-brand" />
                  <input type="email" placeholder="Your Email *" required value={question.email}
                    onChange={(e) => setQuestion((q) => ({ ...q, email: e.target.value }))}
                    className="border border-gray-200 rounded px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-brand" />
                </div>
                <textarea placeholder="Your Question *" required rows={4} value={question.body}
                  onChange={(e) => setQuestion((q) => ({ ...q, body: e.target.value }))}
                  className="w-full border border-gray-200 rounded px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-brand" />
                <button type="submit"
                  className="bg-black text-brand px-8 py-3 font-bold text-sm tracking-widest uppercase hover:bg-brand hover:text-black transition">
                  Submit Question
                </button>
              </form>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
